import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  DuplicateQueryModal,
  UnsavedChangesModal,
} from '@/sections/insights/queryBuilder/components/Modals';
import { QueryResults } from '@/sections/insights/queryBuilder/components/QueryResults';
import { useQueryOperations } from '@/sections/insights/queryBuilder/hooks/useQueryOperations';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

import { EmptyState } from './EmptyState';
import { QuerySplitLayout } from './QuerySplitLayout';
import { QuerySimpleVisualizer } from './QuerySplitVisualizer';
import { ActionsToolbar } from './QuerySplitVisualizer/ActionsToolbar';

/**
 * QuerySplitEditor - Editable query interface with split-pane layout
 *
 * Layout:
 * - Left pane: QuerySimpleVisualizer
 * - Right pane: EmptyState initially, QueryResults after execution
 * - No action panel (as per design requirements)
 *
 * Behavior:
 * - Matches QueryWriter pattern: EmptyState → QueryResults
 * - Uses QuerySplitLayout for consistent split-pane behavior
 * - Storage key: "querySplitEditorRatio"
 *
 * Triggered by:
 * - QueryMenu "Edit Query (Split View)" action
 * - QueryBuilderWorkstation when mode === 'split-edit'
 */
export const QuerySplitEditor = () => {
  const { queryState, isGlobal, getSavedQuery } = useQueryBuilder();
  const [showResults, setShowResults] = useState(false);

  // NEW: Cache the query snapshot from last Execute click
  const [executedQuery, setExecutedQuery] = useState<SavedQuery | null>(null);

  // Add action handlers
  const {
    handleSave,
    isSaving,
    getExecutableQuery,
    pendingOverwrite,
    confirmOverwrite,
    cancelOverwrite,
  } = useQueryOperations();
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  const handleDuplicate = () => {
    setIsDuplicateModalOpen(true);
  };

  const handleExecute = () => {
    const currentQuery = getExecutableQuery();
    if (!currentQuery) {
      return;
    }

    setExecutedQuery(currentQuery);
    setShowResults(true);
  };

  // Reset cache when switching to different query
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Valid use case: resetting UI state when query changes
    setExecutedQuery(null);

    setShowResults(false);
  }, [queryState?.metadata.id]);

  // Error handling for missing query
  if (!queryState) {
    toast.error('No query selected');
    return null;
  }

  return (
    <>
      <QuerySplitLayout
        leftContent={
          <>
            <ActionsToolbar
              editable={true}
              isGlobal={isGlobal}
              onExecute={handleExecute}
              onDuplicate={handleDuplicate}
              onSave={handleSave}
              isSaving={isSaving}
            />
            <div className="relative h-full">
              <QuerySimpleVisualizer editable={true} />
            </div>
          </>
        }
        rightContent={
          showResults ? (
            <QueryResults selectedQuery={executedQuery} />
          ) : (
            <EmptyState />
          )
        }
        storageKey="querySplitEditorRatio"
        testId="query-split-editor"
      />

      {/* Duplicate Modal */}
      {getSavedQuery() && (
        <DuplicateQueryModal
          isOpen={isDuplicateModalOpen}
          onClose={() => setIsDuplicateModalOpen(false)}
          originalQuery={getSavedQuery()!}
        />
      )}

      <UnsavedChangesModal
        isOpen={!!pendingOverwrite}
        onConfirm={() => {
          confirmOverwrite();
        }}
        onCancel={cancelOverwrite}
        title="Overwrite existing query?"
        message="A query with this ID already exists. Do you want to overwrite it?"
        confirmLabel="Overwrite"
        cancelLabel="Cancel"
      />
    </>
  );
};
